// src/private/src/context/dashboard/DailyPulse.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchDailyPulseByClient,
  fetchDailyPulseByCampaign,
  fetchDailyPulseByChannel
} from "@src/api";
import { useAuth } from "@src/context";
import { formatDateToYMD, getCurrentWeekDates } from "@src/utils/dateUtils";

const DailyPulseContext = React.createContext(null);
export const useDailyPulse = () => useContext(DailyPulseContext);

export const DailyPulseProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState([]);
  const [tree, setTree] = useState([]);
  const { isAuthenticated } = useAuth();

  const [monday, sunday] = getCurrentWeekDates();
  const [startDate, setStartDate] = useState(monday);
  const [endDate, setEndDate] = useState(sunday);
  const [orderBy, setOrderBy] = useState("total_spend_sum");
  const [orderDirection, setOrderDirection] = useState("desc");

  const baseFilters = useMemo(() => {
    const filters = {
      date_after: formatDateToYMD(startDate),
      date_before: formatDateToYMD(endDate),
    };
    if (orderBy) {
      filters.ordering = `${orderDirection === 'desc' ? '-' : ''}${orderBy}`;
    }
    return filters;
  }, [startDate, endDate, orderBy, orderDirection]);

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(true);
      fetchDailyPulseByClient(baseFilters).then((resp) => {
        setIsLoading(false);
        setTree(resp.data);
      }).catch((error) => {
        console.error(error);
        setIsLoading(false);
        setIsError(true);
      });
    }
  }, [isAuthenticated, baseFilters]);

  useEffect(() => {
    const arr = [];
    tree.forEach((client) => {
      arr.push(client);
      (client.channels || []).forEach((channel) => {
        arr.push(channel);
        (channel.campaigns || []).forEach((campaign) => {
          arr.push(campaign);
        });
      });
    });
    setData(arr);
  }, [tree]);

  const handleClientClick = (row) => {
    fetchDailyPulseByChannel({
      client_id: row.client_id,
      ...baseFilters
    }).then((resp) => {
      setTree((prev) =>
        prev.map((item) =>
          item.uuid === row.uuid
            ? { ...item, channels: resp.data.map((ch) => ({ ...ch })) }
            : item
        )
      );
    }).catch((err) => {
      console.error("Error fetching channels", err);
    });
  };
  
  

  const handleChannelClick = (row) => {
    const client = tree.find((o) => o.client_id === row.client_id);
    const channels = client?.channels || [];
    const channel = channels.find((o) => o.uuid === row.uuid);
    const campaigns = channel?.campaigns || [];
    if (campaigns.length) {
      setTree((prev) => prev.map((item) => {
        if (item.client === row.client) {
          return {
            ...item,
            channels: item.channels.map((ch) => (
              ch.uuid === row.uuid ? { ...ch, campaigns: [] } : ch
            ))
          };
        }
        return item;
      }));
    } else {
      fetchDailyPulseByCampaign({
        client_id: row.client_id,
        channel_id: row.channel_id,
        ...baseFilters
      }).then((resp) => {
        setTree((prev) => prev.map((item) => {
          if (item.client === row.client) {
            return {
              ...item,
              channels: item.channels.map((ch) => (
                ch.uuid === row.uuid ? { ...ch, campaigns: resp.data } : ch
              ))
            };
          }
          return item;
        }));
      });
    }
  };

  const loadData = (row) => {
    if (row.client__name) {
      handleClientClick(row);
    } else if (row.channel__name) {
      handleChannelClick(row);
    }
  };

  const setDateRange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const setSort = useCallback((field) => {
    if (field === orderBy) {
      setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDirection('asc');
    }
  }, [orderBy]);

  const value = useMemo(() => ({
    isLoading,
    isError,
    data,
    loadData,
    startDate,
    endDate,
    setDateRange,
    orderBy,
    orderDirection,
    setSort
  }), [
    isLoading,
    isError,
    data,
    loadData,
    startDate,
    endDate,
    setDateRange,
    orderBy,
    orderDirection,
    setSort
  ]);

  return (
    <DailyPulseContext.Provider value={value}>
      {children}
    </DailyPulseContext.Provider>
  );
};
